import React from 'react';
import { Maximize2, Minimize2, LayoutGrid, List as ListIcon } from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';

interface BookListToolbarProps {
  displayedBooks: Book[];
  totalBooksCount: number;
  selectedStatus: string;
  expandedBookIds: Record<string, boolean>;
  viewMode: 'grid' | 'list';
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onSetViewMode: (mode: 'grid' | 'list') => void;
}

export const BookListToolbar: React.FC<BookListToolbarProps> = ({
  displayedBooks,
  totalBooksCount,
  selectedStatus,
  expandedBookIds,
  viewMode,
  onExpandAll,
  onCollapseAll,
  onSetViewMode,
}) => {
  const isAllExpanded =
    displayedBooks.length > 0 && displayedBooks.every((b) => expandedBookIds[b.id]);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 text-xs bg-slate-50/80 px-3.5 sm:px-4 py-2.5 rounded-xl border border-slate-200/80">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-800">
          Menampilkan {displayedBooks.length} dari {totalBooksCount} Judul Buku
        </span>
        {selectedStatus !== 'all' && (
          <span className="px-2 py-0.5 rounded-md bg-white border border-slate-200 text-slate-600 text-[11px]">
            Filter: {selectedStatus}
          </span>
        )}
      </div>

      <div className="flex flex-wrap sm:flex-nowrap items-center justify-between sm:justify-end gap-2 w-full sm:w-auto">
        {/* Expand / Collapse All Toggle */}
        <button
          type="button"
          id="btn-toggle-expand-all"
          onClick={() => {
            if (isAllExpanded) {
              onCollapseAll();
            } else {
              onExpandAll();
            }
          }}
          className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-700 border border-slate-200 font-medium transition flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs text-xs min-h-[44px]"
        >
          {isAllExpanded ? (
            <>
              <Minimize2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <span className="hidden sm:inline">Tutup Semua Rincian</span>
              <span className="sm:hidden">Tutup Semua</span>
            </>
          ) : (
            <>
              <Maximize2 className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
              <span className="hidden sm:inline">Buka Semua Rincian ({displayedBooks.length})</span>
              <span className="sm:hidden">Buka Semua ({displayedBooks.length})</span>
            </>
          )}
        </button>

        {/* View Mode Switcher */}
        <div className="flex bg-white p-0.5 rounded-lg border border-slate-200 shadow-2xs shrink-0">
          <button
            type="button"
            id="btn-view-grid"
            onClick={() => onSetViewMode('grid')}
            className={`p-1.5 rounded-md transition cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center ${
              viewMode === 'grid'
                ? 'bg-slate-900 text-white shadow-2xs'
                : 'text-slate-500 hover:text-slate-900'
            }`}
            title="Tampilan Grid Kartu"
          >
            <LayoutGrid className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            id="btn-view-list"
            onClick={() => onSetViewMode('list')}
            className={`p-1.5 rounded-md transition cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center ${
              viewMode === 'list'
                ? 'bg-slate-900 text-white shadow-2xs'
                : 'text-slate-500 hover:text-slate-900'
            }`}
            title="Tampilan Baris / Tabel"
          >
            <ListIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};
